/**
 * Horizontal bars showing top users by total context density (memories +
 * coaching notes + research sources + health datapoints). Anonymized by
 * default; reveals go through RevealIdentifiersToggle upstream.
 */
import React, { useMemo } from 'react';
import { CEDAR } from '../theme';
import { formatCompact, formatInt } from '../format';

export interface LeaderboardRow {
  user_token: string;
  memories: number;
  coaching_notes: number;
  research_sources: number;
  health_datapoints: number;
  /** Proxy approximation: memory_recalls / chat_messages. Real per-turn
   *  datapoint counter is wired in master_context_orchestrator follow-up. */
  memory_recall_density_proxy: number;
  density_total: number;
}

export interface ContextDensityLeaderboardProps {
  rows: LeaderboardRow[];
}

export const ContextDensityLeaderboard: React.FC<ContextDensityLeaderboardProps> = ({ rows }) => {
  const max = useMemo(
    () => rows.reduce((acc, r) => Math.max(acc, r.density_total), 0) || 1,
    [rows],
  );

  if (!rows.length) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-gray-500">
        No context density data yet — wait for the first nightly snapshot.
      </div>
    );
  }

  return (
    <ul className="space-y-1.5">
      {rows.map((r, i) => {
        const pct = (r.density_total / max) * 100;
        return (
          <li key={r.user_token} className="group">
            <div className="flex items-baseline justify-between gap-3">
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="text-[10.5px] font-semibold tabular-nums text-gray-400">
                  #{i + 1}
                </span>
                <span className="truncate font-mono text-[11px] text-gray-700">{r.user_token}</span>
              </div>
              <span className="text-[11px] font-semibold tabular-nums text-gray-800">
                {formatInt(r.density_total)}
              </span>
            </div>
            <div className="mt-0.5 h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${pct}%`,
                  background: `linear-gradient(90deg, ${CEDAR.stream} 0%, ${CEDAR.prime} 100%)`,
                }}
              />
            </div>
            <div className="mt-0.5 flex gap-3 text-[10px] text-gray-400">
              <span>mem {formatCompact(r.memories)}</span>
              <span>notes {formatCompact(r.coaching_notes)}</span>
              <span>research {formatCompact(r.research_sources)}</span>
              <span>health {formatCompact(r.health_datapoints)}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );
};
